"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  reportReviewAction,
  type ReportReason,
} from "@/app/actions/moderation";

type Props = {
  reviewId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReported?: () => void;
};

const REASONS: ReportReason[] = [
  "objectionable",
  "spam",
  "harassment",
  "misinfo",
  "other",
];

export function ReviewReportSheet({
  reviewId,
  open,
  onOpenChange,
  onReported,
}: Props) {
  const t = useTranslations("reviews.report");
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = (next: boolean) => {
    if (submitting) return;
    onOpenChange(next);
    if (!next) {
      // Reset selection when sheet closes so reopening starts fresh.
      setReason(null);
      setDetail("");
    }
  };

  const handleSubmit = async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    try {
      const result = await reportReviewAction(reviewId, reason, detail);
      if ("success" in result) {
        toast.success(t("submitSuccess"));
        onReported?.();
        handleClose(false);
        return;
      }
      // Map error codes to user-facing messages.
      const key =
        result.error === "ALREADY_REPORTED"
          ? "errorAlreadyReported"
          : result.error === "SELF_REPORT"
          ? "errorSelfReport"
          : result.error === "LOGIN_REQUIRED"
          ? "errorLoginRequired"
          : "errorUnknown";
      toast.error(t(key));
    } catch (error) {
      console.error("[ReviewReportSheet] submit failed", error);
      toast.error(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={handleClose}
      title={t("title")}
      description={t("subtitle")}
      drawerHeight="fit"
      footer={
        <div className="flex w-full gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => handleClose(false)}
            disabled={submitting}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
            onClick={handleSubmit}
            disabled={!reason || submitting}
          >
            {submitting ? t("submitting") : t("submit")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-2 text-sm">
        <RadioGroup
          value={reason ?? ""}
          onValueChange={(v) => setReason(v as ReportReason)}
          disabled={submitting}
          className="gap-2"
        >
          {REASONS.map((r) => {
            const inputId = `report-reason-${r}`;
            const selected = reason === r;
            return (
              <Label
                key={r}
                htmlFor={inputId}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors font-normal ${
                  selected
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <RadioGroupItem
                  id={inputId}
                  value={r}
                  className="mt-0.5 border-gray-400 text-orange-500 data-[state=checked]:border-orange-500"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {t(`reasons.${r}.label`)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t(`reasons.${r}.description`)}
                  </p>
                </div>
              </Label>
            );
          })}
        </RadioGroup>

        <div className="space-y-1.5">
          <Label htmlFor="report-detail" className="text-gray-700">
            {t("detailLabel")}
          </Label>
          {/* ui Textarea: 한글 IME 조합 보정(안드로이드 한 글자 지연 방지) 포함 */}
          <Textarea
            id="report-detail"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            disabled={submitting}
            maxLength={500}
            placeholder={t("detailPlaceholder")}
            rows={3}
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
          />
          <p className="text-right text-xs text-gray-400">
            {detail.length}/500
          </p>
        </div>
      </div>
    </ResponsiveModal>
  );
}
